import { useParams } from 'react-router-dom';
import { useModalContext } from 'utils/contexts/modal';
import noop from 'utils/noop';
import { usePurchaseMappingsContext, useUserMappingsContext } from '../contexts';
import { useCustomEventDetailsContext } from '../contexts/CustomEventDetailsContext';
import { EditFieldFormProvider } from '../contexts/EditFieldFormProvider';
import { MappingFieldFormProvider } from '../contexts/MappingFieldFormProvider';
import { FieldProperty, ModalAction, ModalState } from '../types';
import { CreateCustomEventFieldModal, CreateUserFieldModal, SaveSettingsModal } from './modals';
import { CreatePurchaseFieldModal } from './modals/CreatePurchaseFieldModal';
import { EditPropertyModal } from './modals/EditPropertyModal';

export const UserProfileModals = () => {
  const { modalState } = useModalContext<ModalState>();
  const { userMappings } = useUserMappingsContext();
  const { purchaseMappings } = usePurchaseMappingsContext();
  const { customEventDetails } = useCustomEventDetailsContext();
  const { customEventName } = useParams<{ customEventName: string }>();

  if (!modalState) return null;

  const defaultPayload = {
    field: undefined,
    fieldProperty: FieldProperty.DESCRIPTION,
    onSave: noop,
    parentName: '',
  };

  const { action = null, payload = defaultPayload } = modalState;
  if (!action) return null;

  const { onSave, parentName = '' } = payload;

  switch (action) {
    case ModalAction.NEW_USER_FIELD:
      return (
        <MappingFieldFormProvider
          fields={userMappings.map(({ fieldName, fieldType }) => ({ fieldName, fieldType }))}
          parentName={parentName}
        >
          <CreateUserFieldModal parentName={parentName} />
        </MappingFieldFormProvider>
      );

    case ModalAction.NEW_EVENT_FIELD:
      return (
        <MappingFieldFormProvider
          fields={customEventDetails.map(({ fieldName, fieldType }) => ({
            fieldName: fieldName.replace(`${customEventName}.`, ''),
            fieldType,
          }))}
          parentName={parentName}
        >
          <CreateCustomEventFieldModal parentName={parentName} />
        </MappingFieldFormProvider>
      );

    case ModalAction.NEW_PURCHASE_FIELD:
      return (
        <MappingFieldFormProvider
          fields={purchaseMappings.map(({ fieldName, fieldType }) => ({ fieldName, fieldType }))}
          parentName={parentName}
        >
          <CreatePurchaseFieldModal parentName={parentName} />
        </MappingFieldFormProvider>
      );
    case ModalAction.EDIT_PROPERTY:
      return (
        <EditFieldFormProvider field={payload?.field}>
          <EditPropertyModal
            field={payload?.field}
            fieldProperty={payload?.fieldProperty ?? FieldProperty.DESCRIPTION}
          />
        </EditFieldFormProvider>
      );

    case ModalAction.SAVE_SETTINGS:
      return <SaveSettingsModal onSave={onSave ?? noop} />;

    default:
      return <></>;
  }
};
